import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorIcon from '@mui/icons-material/Error';
import FolderIcon from '@mui/icons-material/Folder';
import InfoIcon from '@mui/icons-material/Info';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningIcon from '@mui/icons-material/Warning';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, Button, Grid, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useState } from 'react';
import {
  DocumentDetailResponse,
  DocumentHealthStatus,
  UpdateMessageType,
} from '../../../../interfaces/document';
import referralService from '../../../../services/referral';
import FieldDisplay from '../../FieldDisplay';
import StatusChip from '../../StatusChip';
import { DOCUMENT_TYPE_COLORS } from '../../constants';

interface DocumentInformationTabProps {
  document: DocumentDetailResponse;
}

interface DuplicateInfo {
  isDuplicate: boolean;
  duplicateOfReferralId?: number;
  clinicInternalDocumentId?: string;
  originalDocumentId?: number;
  isResolved?: boolean;
}

const detectDuplicateFromProcessingHistory = (document: DocumentDetailResponse): DuplicateInfo => {
  if (!document.processingHistory || document.processingHistory.length === 0) {
    return { isDuplicate: false };
  }

  // Check processingHistory for duplicate metadata
  const duplicateEntry = document.processingHistory?.find(
    (history) => history.processingMetadata?.isDuplicate === true,
  );

  if (!duplicateEntry?.processingMetadata) {
    return { isDuplicate: false };
  }

  const mostRecentEntry = document.processingHistory[0];
  const isResolved = mostRecentEntry.processingMetadata?.requiresHumanReview === false;

  return {
    isDuplicate: true,
    duplicateOfReferralId: duplicateEntry.processingMetadata.duplicateOf,
    clinicInternalDocumentId: duplicateEntry.processingMetadata.clinicInternalDocumentId,
    originalDocumentId: duplicateEntry.processingMetadata.originalDocumentId,
    isResolved,
  };
};

export const DocumentInformationTab: React.FC<DocumentInformationTabProps> = ({ document }) => {
  const [updateMessage, setUpdateMessage] = useState<{
    type: UpdateMessageType;
    text: string;
  } | null>(null);

  /**
   * Handles marking a duplicate document as reviewed
   */
  const handleMarkAsReviewed = async () => {
    try {
      await referralService.markDocumentAsResolved(
        document.documentId,
        'Duplicate document reviewed and marked as resolved',
      );
      setUpdateMessage({
        type: UpdateMessageType.Success,
        text: 'Document marked as resolved successfully',
      });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Error marking document as resolved:', error);
      setUpdateMessage({
        type: UpdateMessageType.Error,
        text: 'Failed to mark document as resolved',
      });
    }
  };

  const formatDate = (dateString: string): string => {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
  };

  const getTotalPatientCount = () => {
    return (
      document.detectedDocuments?.reduce(
        (total, dd) => total + (dd.patientDocuments?.length || 0),
        0,
      ) || 0
    );
  };

  const getDetectedClinicIds = () => {
    const clinicIds = new Set(
      document.detectedDocuments?.map((dd) => dd.clinicId).filter(Boolean) || [],
    );
    return Array.from(clinicIds).join(', ') || 'None';
  };

  const getDocumentTypes = () => {
    return (
      document.detectedDocuments?.map((dd) => ({
        type: dd.documentType || 'Unknown',
        confidence: dd.confidence || 0,
        clinicId: dd.clinicId,
      })) || []
    );
  };

  const getOverallHealth = () => {
    if (document.requiresReview) {
      return {
        status: DocumentHealthStatus.Warning,
        message: 'Requires Review',
        color: '#f59e0b',
      };
    }
    if (document.overallProcessingStatus === 'failed') {
      return { status: DocumentHealthStatus.Error, message: 'Processing Failed', color: '#ef4444' };
    }
    if (document.overallProcessingStatus === 'completed') {
      return {
        status: DocumentHealthStatus.Success,
        message: 'Completed Successfully',
        color: '#10b981',
      };
    }
    return { status: DocumentHealthStatus.Info, message: 'In Progress', color: '#0891b2' };
  };

  const health = getOverallHealth();
  const documentTypes = getDocumentTypes();
  const duplicateInfo = detectDuplicateFromProcessingHistory(document);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h6' sx={{ mb: 3 }}>
        Document Overview
      </Typography>

      {/* Update Message Alert */}
      {updateMessage && (
        <Alert severity={updateMessage.type} sx={{ mb: 3 }} onClose={() => setUpdateMessage(null)}>
          {updateMessage.text}
        </Alert>
      )}

      {/* Duplicate Document Alert */}
      {duplicateInfo.isDuplicate && (
        <Alert
          severity='warning'
          icon={<WarningAmberIcon />}
          sx={{
            mb: 3,
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box>
              <Typography variant='subtitle2' sx={{ fontWeight: 600, mb: 0.5 }}>
                Duplicate Document Detected
              </Typography>
              <Typography variant='body2'>
                This document is a duplicate of an existing referral
                {duplicateInfo.duplicateOfReferralId &&
                  ` (Referral ID: ${duplicateInfo.duplicateOfReferralId})`}
                {duplicateInfo.clinicInternalDocumentId &&
                  ` with clinic document ID: ${duplicateInfo.clinicInternalDocumentId}`}
                .
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {duplicateInfo.originalDocumentId && (
                <Button
                  size='small'
                  variant='outlined'
                  startIcon={<OpenInNewIcon />}
                  onClick={() => {
                    // Navigate directly to the original document
                    window.open(
                      `#/documents/library/${duplicateInfo.originalDocumentId}`,
                      '_blank',
                    );
                  }}
                  sx={{
                    borderColor: '#f59e0b',
                    color: '#f59e0b',
                    '&:hover': {
                      borderColor: '#d97706',
                      bgcolor: '#fffbeb',
                    },
                  }}>
                  View Original Referral
                </Button>
              )}
              {/* Only show "Mark as Reviewed" button if not already resolved */}
              {!duplicateInfo.isResolved && (
                <Button
                  size='small'
                  variant='contained'
                  onClick={handleMarkAsReviewed}
                  sx={{
                    bgcolor: '#f59e0b',
                    '&:hover': {
                      bgcolor: '#d97706',
                    },
                  }}>
                  Mark as Reviewed
                </Button>
              )}
            </Box>
          </Box>
        </Alert>
      )}

      {/* Status Alert */}
      <Alert
        severity={health.status as any}
        icon={
          health.status === DocumentHealthStatus.Warning ? (
            <WarningIcon />
          ) : health.status === DocumentHealthStatus.Error ? (
            <ErrorIcon />
          ) : health.status === DocumentHealthStatus.Success ? (
            <CheckCircleIcon />
          ) : (
            <InfoIcon />
          )
        }
        sx={{ mb: 4 }}>
        <Typography variant='body2' fontWeight='500'>
          {health.message}
        </Typography>
        {document.requiresReview && (
          <Typography variant='caption' display='block' sx={{ mt: 0.5 }}>
            This document needs human review to proceed
          </Typography>
        )}
      </Alert>

      {/* Document Basics */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            pb: 1,
            borderBottom: '2px solid',
            borderColor: 'divider',
          }}>
          <DescriptionIcon sx={{ mr: 1 }} color='primary' />
          <Typography variant='h6'>Document Basics</Typography>
        </Box>
        <Grid container spacing={3}>
          <FieldDisplay
            label='Document ID'
            value={document.externalId || document.documentId}
            gridSize={{ xs: 12, md: 6 }}
            variant='simple'
            fontWeight='semibold'
          />
          <FieldDisplay
            label='Source'
            value={document.source?.replace('-', ' ') || 'Unknown'}
            gridSize={{ xs: 6, md: 3 }}
            variant='simple'
          />
          <FieldDisplay
            label='Created'
            value={formatDate(document.documentCreatedAt)}
            gridSize={{ xs: 6, md: 3 }}
            variant='simple'
          />
          <FieldDisplay
            label='Overall Status'
            value={document.overallProcessingStatus?.replace('-', ' ')}
            gridSize={{ xs: 6, md: 3 }}
            variant='simple'
            renderAsChip={true}
            status={
              document.overallProcessingStatus === 'completed'
                ? 'success'
                : document.overallProcessingStatus === 'failed'
                ? 'error'
                : 'info'
            }
          />
        </Grid>
      </Box>

      {/* Clinical Information */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            pb: 1,
            borderBottom: '2px solid',
            borderColor: 'divider',
          }}>
          <LocalHospitalIcon sx={{ mr: 1 }} color='primary' />
          <Typography variant='h6'>Clinical Information</Typography>
        </Box>
        <Grid container spacing={3}>
          <FieldDisplay
            label='Receiving Clinic'
            value={
              document.receivingClinicId ? `Clinic ${document.receivingClinicId}` : 'Not assigned'
            }
            gridSize={{ xs: 6, md: 3 }}
            variant='simple'
          />
          <FieldDisplay
            label='Detected Clinics'
            value={getDetectedClinicIds()}
            gridSize={{ xs: 6, md: 3 }}
            variant='simple'
          />
          <FieldDisplay
            label='Total Patients'
            value={getTotalPatientCount()}
            gridSize={{ xs: 6, md: 3 }}
            variant='simple'
            fontWeight='semibold'
          />
          <FieldDisplay
            label='Processing Step'
            value={document.currentProcessingStep?.replace('-', ' ') || 'Unknown'}
            gridSize={{ xs: 6, md: 3 }}
            variant='simple'
          />
        </Grid>
      </Box>

      {/* Detected Document Types */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: 3,
            pb: 1,
            borderBottom: '2px solid',
            borderColor: 'divider',
          }}>
          <FolderIcon sx={{ mr: 1 }} color='primary' />
          <Typography variant='h6'>Detected Document Types</Typography>
        </Box>
        {documentTypes.length > 0 ? (
          <Grid container spacing={2}>
            {documentTypes.map((docType, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <StatusChip
                      label={docType.type.replace('-', ' ')}
                      variant='outlined'
                      color={
                        docType.type === 'referral-order'
                          ? DOCUMENT_TYPE_COLORS.REFERRAL_ORDER.COLOR
                          : docType.type === 'prior-authorization'
                          ? DOCUMENT_TYPE_COLORS.PRIOR_AUTHORIZATION.COLOR
                          : DOCUMENT_TYPE_COLORS.DEFAULT.COLOR
                      }
                      backgroundColor={
                        docType.type === 'referral-order'
                          ? DOCUMENT_TYPE_COLORS.REFERRAL_ORDER.BACKGROUND
                          : docType.type === 'prior-authorization'
                          ? DOCUMENT_TYPE_COLORS.PRIOR_AUTHORIZATION.BACKGROUND
                          : DOCUMENT_TYPE_COLORS.DEFAULT.BACKGROUND
                      }
                      borderColor={
                        docType.type === 'referral-order'
                          ? DOCUMENT_TYPE_COLORS.REFERRAL_ORDER.BORDER
                          : docType.type === 'prior-authorization'
                          ? DOCUMENT_TYPE_COLORS.PRIOR_AUTHORIZATION.BORDER
                          : DOCUMENT_TYPE_COLORS.DEFAULT.BORDER
                      }
                      fontWeight='medium'
                    />
                  </Box>
                  <Typography variant='caption' color='text.secondary'>
                    {Math.round(docType.confidence * 100)}% confidence
                  </Typography>
                  {docType.clinicId && (
                    <Typography variant='caption' display='block' color='text.secondary'>
                      Clinic: {docType.clinicId}
                    </Typography>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            No document types detected
          </Typography>
        )}
      </Box>
    </Box>
  );
};
